import { evaluateFormState } from "../ruleEngine";

export function buildDynamicResolver(fieldConfigs, contextValues = {}) {
  return async (values) => {
    const errors = {};

    const mergedValues = { ...contextValues, ...values };
    const derivedState = evaluateFormState(mergedValues, fieldConfigs);

    for (const config of fieldConfigs) {
      const fieldState = derivedState[config.fieldKey];

      if (!fieldState || !fieldState.visible) continue;

      const value = values[config.fieldKey];

      for (const rule of config.validation ?? []) {
        const message = evaluateRule(rule, value, values, config, fieldState);
        if (message) {
          errors[config.fieldKey] = { type: rule.rule, message };
          break;
        }
      }
    }

    return {
      values: Object.keys(errors).length === 0 ? values : {},
      errors,
    };
  };
}

export function evaluateLiveValidation(config, value, allValues, fieldState) {
  for (const rule of config.validation ?? []) {
    if (rule.rule === 'required') continue;
    const message = evaluateRule(rule, value, allValues, config, fieldState);
    if (message) return message;
  }
  return null;
}

function evaluateRule(rule, value, allValues, config, fieldState) {
  switch (rule.rule) {
    case "required": {
      const isEmpty =
        value === undefined ||
        value === null ||
        value === "" ||
        value === "-1" ||
        (Array.isArray(value) && value.length === 0);

      if (!isEmpty) return null;

      if (config.group === "Name") {
        return getShortRequiredMessage(config.label);
      }

      return rule.message || `${config.label} is required.`;
    }

    case "maxLength": {
      if (!value) return null;
      if (String(value).length > rule.value) {
        return rule.message || `${config.label} cannot exceed ${rule.value} characters.`;
      }
      return null;
    }

    case "email": {
      if (!value) return null;
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(String(value))) {
        return rule.message || "Please enter a valid email address.";
      }
      return null;
    }

    case "regex": {
      if (!value) return null;
      try {
        const pattern = new RegExp(rule.value);
        if (!pattern.test(String(value))) {
          return rule.message || "Invalid format.";
        }
      } catch (e) {
        console.warn(
          `[FieldEngine] Invalid regex pattern in rule for field "${config.fieldKey}":`,
          rule.value,
        );
      }
      return null;
    }

    case "maxDate": {
      if (!value) return null;
      const maxDate =
        rule.value === "today" ? new Date() : new Date(rule.value);
      maxDate.setHours(23, 59, 59, 999);
      if (new Date(value) > maxDate) {
        return rule.message || `${config.label} cannot be a future date.`;
      }
      return null;
    }

    case "minDate": {
      if (!value) return null;
      const minDate =
        rule.value === "today" ? new Date() : new Date(rule.value);
      minDate.setHours(0, 0, 0, 0);
      if (new Date(value) < minDate) {
        return (
          rule.message || `${config.label} cannot be before ${rule.value}.`
        );
      }
      return null;
    }

    case "gte": {
      if (!value || !rule.compareTo) return null;
      const compareValue = allValues[rule.compareTo];
      if (!compareValue) return null;
      if (new Date(value) < new Date(compareValue)) {
        return (
          rule.message ||
          `${config.label} must be on or after the compared date.`
        );
      }
      return null;
    }

    case "lte": {
      if (!value || !rule.compareTo) return null;
      const compareValue = allValues[rule.compareTo];
      if (!compareValue) return null;
      if (new Date(value) > new Date(compareValue)) {
        return (
          rule.message ||
          `${config.label} must be on or before the compared date.`
        );
      }
      return null;
    }

    case "crossField": {
      if (!rule.conditionField || !rule.conditionValue || !rule.compareTo)
        return null;
      const triggerValue = allValues[rule.conditionField];
      if (triggerValue !== rule.conditionValue) return null;

      const dependentValue = allValues[rule.compareTo];
      const isEmpty =
        !dependentValue || dependentValue === "" || dependentValue === "-1";
      if (isEmpty) {
        return rule.message || "This field is required.";
      }
      return null;
    }

    case "legacy": {
      if (!rule.rawScript) return null;
      try {
        const fieldValues = Object.assign({}, allValues);
        // eslint-disable-next-line no-new-func
        const guardedJQuery = createReadOnlyValJQuery();
        const legacyFn = new Function("values", "fieldKey", "$", "jQuery", rule.rawScript);
        const result = legacyFn(fieldValues, config.fieldKey, guardedJQuery, guardedJQuery);
        if (result && result.flag === false) {
          return result.msg || rule.message || "Validation failed.";
        }
      } catch (e) {
        console.warn(
          `[FieldEngine] Legacy pre_save_script eval failed for "${config.fieldKey}":`,
          e,
        );
      }
      return null;
    }

    default:
      console.warn(
        `[FieldEngine] Unknown validation rule: "${rule.rule}" on field "${config.fieldKey}"`,
      );
      return null;
  }
}

function createReadOnlyValJQuery() {
  const realJQuery = window.$ || window.jQuery;
  if (!realJQuery) return undefined;

  const wrapSet = (set) => {
    const wrapped = new Proxy(set, {
      get(target, prop) {
        if (prop === "val") {
          return (...args) => (args.length === 0 ? target.val() : wrapped);
        }
        const member = target[prop];
        if (typeof member !== "function") return member;
        return (...args) => {
          const result = member.apply(target, args);
          return result === target ? wrapped : result;
        };
      },
    });
    return wrapped;
  };

  const guarded = (...args) => {
    const result = realJQuery(...args);
    return result && result.jquery ? wrapSet(result) : result;
  };
  return Object.assign(guarded, realJQuery);
}

function getShortRequiredMessage(fieldLabel) {
  const shortLabel = fieldLabel.includes(" ")
    ? fieldLabel.substring(fieldLabel.indexOf(" ") + 1)
    : fieldLabel;

  return `${shortLabel} is required.`;
}
